/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import React from 'react';
import { Redirect, Route, RouteComponentProps, Switch, useLocation, useRouteMatch } from 'react-router';
import { CluProjectPage } from 'src/clu/appShell/CluProjectPage';
import { CluProjectsListPage } from 'src/clu/appShell/cluProjectsListPage/CluProjectsListPage';
import { CluBreadcrumb } from 'src/clu/modules/components/CluBreadcrumb';
import { CluSidebar } from 'src/clu/modules/components/CluSidebar';
import { useCluStartupConfigs } from 'src/clu/modules/hooks/useCluStartupConfigs';
import { NotFoundRedirect } from 'src/core/components/NotFoundRoute';
import { PrivateRoute } from 'src/core/components/PrivateRoute';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { getStylist } from 'src/core/themes/luisStylist';
import { routeTable } from 'src/core/utils/routeTable';
import { doesRouteMatchLocation } from 'src/core/utils/routingUtils';

const { styleDiv, styleComponent } = getStylist('CluRoutes');
const BreadcrumbWrapper = styleDiv('CluBreadcrumbWrapper', { paddingLeft: 24 });
const CluRoutesContainer = styleComponent<IStackProps>(Stack)('CluRoutesContainer', { overflow: 'hidden' });
const CluContentContainer = styleComponent<IStackProps>(Stack)('CluContentContainer', { overflow: 'hidden' });

export const CluRoutes = () => {
    const { pathname } = useLocation();
    const { rootStore } = useGlobalState();

    useCluStartupConfigs();

    const { isSignedIn } = rootStore.authStore;
    const isOrchestrationService = doesRouteMatchLocation(routeTable.orchestration.root(), { exact: false, locationToMatch: pathname });
    const isNotDataTaggingPage =
        useRouteMatch(
            routeTable[isOrchestrationService ? 'orchestration' : 'clu'].dataTaggingPage(rootStore.currentCluProjectStore?.id)
        ) === null;

    const renderCluProjectPage = (props: RouteComponentProps<{ projectName: string }>) => {
        const { params } = props.match;
        let encodedCsAccountPath = new URLSearchParams(props.location.search).get('csAccountPath');
        // Azure Cognitive Service for Language - Token theft vulnerability in Azure language studio and override by @ symbol lead to Resource takeover
        if (encodedCsAccountPath !== null && !encodedCsAccountPath.startsWith('/')) {
            encodedCsAccountPath = `/${encodedCsAccountPath}`;
        }
        const csAccountPath = encodedCsAccountPath !== null ? decodeURIComponent(encodedCsAccountPath) : undefined;

        return (
            <CluProjectPage
                csAccountPath={csAccountPath}
                projectName={params.projectName}
                serviceType={isOrchestrationService ? 'Orchestration' : 'Conversation'}
            />
        );
    };

    const renderCluProjectsPage = () => <CluProjectsListPage serviceType={isOrchestrationService ? 'Orchestration' : 'Conversation'} />;

    return (
        <CluRoutesContainer horizontal grow>
            <CluSidebar serviceType={isOrchestrationService ? 'Orchestration' : 'Conversation'} />

            <CluContentContainer grow>
                {isNotDataTaggingPage && (
                    <BreadcrumbWrapper>
                        <CluBreadcrumb serviceType={isOrchestrationService ? 'Orchestration' : 'Conversation'} />
                    </BreadcrumbWrapper>
                )}

                <Switch>
                    {!isSignedIn && [
                        <Route
                            render={renderCluProjectsPage}
                            path={[routeTable.clu.projectsPage(), routeTable.orchestration.projectsPage()]}
                        />
                    ]}
                    <PrivateRoute
                        render={renderCluProjectPage}
                        path={[routeTable.clu.projectPage(), routeTable.orchestration.projectPage()]}
                    />
                    <PrivateRoute exact path={routeTable.clu.projectsPage()} render={renderCluProjectsPage} />
                    <PrivateRoute exact path={routeTable.orchestration.projectsPage()} render={renderCluProjectsPage} />

                    <Redirect from={routeTable.clu.root()} to={routeTable.clu.projectsPage()} />
                    <Redirect from={routeTable.orchestration.root()} to={routeTable.orchestration.projectsPage()} />
                    <NotFoundRedirect />
                </Switch>
            </CluContentContainer>
        </CluRoutesContainer>
    );
};
